@if (title) {
  <div
    class="flex flex-wrap justify-between items-center p-4 max-sm:items-start"
    style="border-bottom: 1px solid var(--mat-sys-outline-variant)"
  >
    <h2
      class="font-normal m-0 order-1 w-1/2 max-sm:w-full"
      [ngClass]="titleClass || 'text-2xl'"
      [id]="'h2' + (widgetId || '_defaultId')"
    >
      {{ title }}
    </h2>
    <div
      class="order-2 w-1/2 flex flex-wrap justify-end gap-2 items-center max-sm:w-full max-sm:mt-1 max-sm:justify-end"
    >
      @if (secondaryActionLabel) {
        <a
          matButton
          class="edit-toggle underline!"
          tabindex="0"
          (click)="onClickSecondaryAction.emit()"
          (keydown.enter)="onClickSecondaryAction.emit()"
          (keydown.space)="onClickSecondaryAction.emit()"
          [attr.aria-label]="secondaryActionLabel"
          [matTooltip]="secondaryActionLabel"
        >
          {{ secondaryActionLabel }}
        </a>
      }
      @if (actionType && actionLabel) {
        @switch (actionType) {
          @case ('link') {
            <button
              type="button"
              matButton
              class="edit-toggle underline!"
              [disabled]="actionDisabled"
              (click)="onClickAction.emit()"
              [attr.aria-label]="actionLabel + ' ' + (title | translate)"
              [matTooltip]="actionLabel + ' ' + (title | translate)"
            >
              {{ actionLabel }}
              <mat-icon iconPositionEnd class="ms-2 material-icons-outlined"
                >arrow_forward</mat-icon
              >
            </button>
          }
          @default {
            <button
              [attr.aria-label]="actionLabel"
              type="button"
              matButton="filled"
              [disabled]="actionDisabled"
              (click)="onClickAction.emit()"
              [matTooltip]="actionLabel"
            >
              {{ actionLabel }}
              <mat-icon>add</mat-icon>
            </button>
          }
        }
      }
    </div>
  </div>
}

<ng-content select="siat-search-bar"></ng-content>

<div
  class="relative overflow-x-auto"
  [ngClass]="hideHeaderMarginTop ? 'mt-0!' : 'mt-4!'"
  siatEqualStatusBadgeWidth
>
  <ng-content></ng-content>
  @if (dataLoading$ | async) {
    <figcaption
      class="absolute inset-0 flex items-center justify-center rounded-2xl"
    ></figcaption>
  }
  <table
    mat-table
    [dataSource]="rows"
    [multiTemplateDataRows]="true"
    [id]="id ?? widgetId"
    class="w-full text-sm text-left"
    [attr.aria-label]="
      ('GENERAL.tabella' | translate) + ' ' + (labelAria | translate)
    "
  >
    @if (expansionTemplate()) {
      <ng-container matColumnDef="expand">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let row" class="w-[10%]!">
          <button
            type="button"
            mat-icon-button
            [attr.aria-label]="expandableRowTooltip | translate"
            (click)="expansionTemplate() ? toggleRow(row) : null"
            [class.cursor-pointer]="expansionTemplate()"
            [matTooltip]="
              expansionTemplate() ? (expandableRowTooltip | translate) : ''
            "
          >
            <mat-icon>{{
              expandedElement === row ? 'arrow_drop_down' : 'arrow_right'
            }}</mat-icon>
          </button>
        </td>
      </ng-container>
    }
    @for (col of column; track col.key) {
      <ng-container [matColumnDef]="col.key" [stickyEnd]="col.sticky === 'end'">
        @if (col.sortable) {
          <th
            scope="col"
            [style.width]="col.width"
            [style.min-width]="col.width"
            class="px-4 py-3 cursor-pointer select-none"
            [style.text-align]="col.align ?? 'left'"
            [siatSortable]="col.sortable"
            (sort)="onSort($event)"
            mat-header-cell
            *matHeaderCellDef
            [attr.role]="col.sortable ? 'button' : 'cell'"
            [title]="col.label | translate"
          >
            <div class="font-semibold">{{ col.label }}</div>
          </th>
        } @else {
          <th
            scope="col"
            [style.width]="col.width"
            class="px-4 py-3"
            [style.text-align]="col.align ?? 'left'"
            mat-header-cell
            *matHeaderCellDef
          >
            <div class="font-semibold">{{ col.label }}</div>
          </th>
        }
        <td
          class="px-4 py-3"
          [style.text-align]="col.align ?? 'left'"
          mat-cell
          *matCellDef="let row"
        >
          @if (customCellTemplates()[col.key]; as tpl) {
            <ng-container
              *ngTemplateOutlet="tpl; context: { $implicit: row }"
            ></ng-container>
          } @else {
            <span>{{ getNestedValue(row, col.key) || '&mdash;' }}</span>
          }
        </td>
      </ng-container>
    }
    @if (expansionTemplate()) {
      <ng-container matColumnDef="expandedDetail">
        <td
          mat-cell
          *matCellDef="let row"
          [attr.colspan]="getColumns(column).length"
        >
          <div
            style="border-top-style: solid"
            class="border-t border-primary w-full detail-row-wrapper"
            [ngClass]="{ 'border-t-0': expandedElement !== row }"
            [class.expanded]="row === expandedElement"
          >
            <div class="detail-row-content">
              <ng-container
                *ngTemplateOutlet="
                  expansionTemplate();
                  context: { $implicit: row }
                "
              ></ng-container>
            </div>
          </div>
        </td>
      </ng-container>
    }

    <tr
      mat-header-row
      *matHeaderRowDef="getColumns(column)"
      class="w-full"
      aria-label="table header"
    ></tr>

    <tr
      mat-row
      aria-label="table body"
      *matRowDef="let row; columns: getColumns(column)"
      [class.expanded-row]="expandedElement === row"
      [class.highlighted-row]="highlightRow?.(row)"
      [ngClass]="{
        'even-row': rows.indexOf(row) % 2 === 0,
        'odd-row': rows.indexOf(row) % 2 !== 0,
      }"
    ></tr>

    @if (expansionTemplate()) {
      <tr
        mat-row
        *matRowDef="let row; columns: ['expandedDetail']"
        class="detail-row"
      ></tr>
    }

    <tr *matNoDataRow>
      <td
        class="mat-cell p-4 text-center bg-white text-gray-500"
        [attr.colspan]="getColumns(column).length"
      >
        {{ emptyMessage || ('GENERAL.no_results_found' | translate) }}
      </td>
    </tr>
  </table>
</div>
@if (pagination) {
  <siat-pagination
    [length]="pagination?.totalElements"
    [pageIndex]="pagination?.number"
    [pageSize]="pagination?.size"
    [totalPages]="pagination?.totalPages"
    [arrowForward]="pagination?.rightPage"
    (page)="pageChange.emit($event)"
  ></siat-pagination>
}
